/* The order record itself — `/sales/orders/:id`'s content, and later a drawer over the list
   (ORD-3) reusing the same component. Takes finished data (`item`, already-loaded) and a `lang`;
   knows nothing about `:id`, the request, or the loading/error states — that is `Page.js`'s job.

   Read-only: the scope excludes create/edit/delete/status changes entirely (1C is the source of
   truth), so unlike every other archetype-B page in this app there is no dirty state and no save
   bar — closer in shape to `/answers/:id` (no sticky command bar) than to a form.

   Layout, as an order page reads elsewhere (Shopify, Stripe): the head (number, status, order type,
   external id, date) and the tiles (amount due, lines, discount, delivery date); then the items with
   their summary, the comment, and short titled groups — "Деталі", "Контрагент", "Доставка"
   (`OrderGroups`). On the page the groups are `Page.js`'s rail, with the map; in the modals one
   column under the items. Each value is on the screen once, and none of them is lost.

   Field shapes come from the ACTUAL `excalibur/orders/{id}/` response: `status` has no
   `status_display`, `order_type` and `counterparty` are nested objects, `point` and `supervisor`
   come through the visit (null without one); `department` / `created` / `modified` are absent.

   Money: the API counts all of it (Decimal, rounded as the mobile app does) and this file only
   formats it. An item's `amount` is its UNIT price and `line_total` its line sum; a line 1C has
   recalculated carries `recalculated_price` / `recalculated_line_total` (else null), shown in the same
   cell with the old value struck through and muted under it. The summary is the items side —
   `order_total`, −`items_discount`, the signed `recalculated_total_delta` — and ends in `payable_total`,
   the figure the head's "До сплати" tile and the list show too. Delivery is not a summary row:
   `shipping_cost` sits in the general information, beside the delivery date. */

import React from "react";
import { Flex, Table, Tag, Typography } from "antd";
import { LinkOutlined, ShoppingCartOutlined, SyncOutlined, WarningOutlined } from "@ant-design/icons";

import { DetailGroup, KpiStrip } from "../../component/detail-page";
import { PersonCell, StatusIcon, dash, textOf } from "../../component/table-cells";
import { ORDERS_PAGE } from "../../config/lang";
import dayjs from "../../util/dayjs";
import { formatMoney } from "../../util/formatter";
import { translate } from "../../util/util";

import { parseGeo } from "./GeoSection";
import { STATUS_ICON } from "./parts";

const recalculated = row => row.recalculated_line_total !== null && row.recalculated_line_total !== undefined;
const isZero = v => v === null || v === undefined || Number(v) === 0;

/** A money cell: the value, and — when it was recalculated — the old one struck through under it. */
const MoneyCell = ({ value, was }) =>
  was === undefined ? (
    <span className="lp-num">{value || dash}</span>
  ) : (
    <>
      <div className="lp-num">{value || dash}</div>
      <del className="lp-num dp-was">{was}</del>
    </>
  );

/** A signed amount — the typographic minus (U+2212), "+" for a rise. */
const signed = v => `${Number(v) < 0 ? "\u2212" : "+"}${formatMoney(Math.abs(Number(v)))}`;
/** A percentage, a space before the sign — the discount tile and the order's discount row alike. */
const percent = (v, digits) => `${Number(v).toFixed(digits)}\u00a0%`;

/** items/units, once — the header's KPI strip is the only current reader, but kept as a named
    helper (mirrors `returns/ReturnDetail.js`'s own `itemTotals`) rather than inlined, so a second
    reader doesn't quietly recompute it a different way. */
const itemTotals = item => ({
  itemsCount: item.items?.length || 0,
  unitsCount: (item.items || []).reduce((sum, row) => sum + (Number(row.quantity) || 0), 0),
});

/** The page head — entity icon, number, status, order type, the 1C reference, and the KPI strip.
    Its own export because `Page.js` needs it OUTSIDE `dp-grid` (spec/03 archetype B), and because
    the list's record modal reuses it verbatim as the antd `Modal` `title` — one header for the
    record, not a page version and a separate, thinner modal version that drift apart. Icon is
    `ShoppingCartOutlined`, the same glyph `AppNav.js` uses for the "Замовлення" nav row. */
export function OrderDetailHead({ item, lang }) {
  const t = key => translate(ORDERS_PAGE, key, lang);
  const { Icon, tone } = STATUS_ICON[item.status] || STATUS_ICON.new;
  const statusLabel = translate(ORDERS_PAGE, `order_status_${item.status}`, lang) || item.status;
  const { itemsCount, unitsCount } = itemTotals(item);

  return (
    <div className="dp-head dp-head--fluid">
      <div className="dp-head__body">
        <div className="dp-head__top">
          <div className={`dp-head__icon dp-head__icon--${tone}`}>
            <ShoppingCartOutlined />
          </div>
          <div className="dp-head__main">
            <div className="dp-head__titlerow">
              <h1 className="dp-title">{item.name ? textOf(item.name) : `#${item.id}`}</h1>
              {/* The filled pill `visit/parts.js`'s `StatusTag` already established — a `-bg`
                 tint plus a `-700` label plus an icon, never colour alone (spec/01#status-colour). */}
              <Tag variant="filled" icon={<Icon />} className={`status-tag status-tag--${tone}`}>
                {statusLabel}
              </Tag>
              {/* Order type is our own classification, not a verdict — a plain bordered pill,
                 same weight as the external reference beside it, neither one a status. */}
              {item.order_type?.name ? <Tag>{textOf(item.order_type.name)}</Tag> : null}
              {/* Deliberately names no system: `external_id` is this record's id in whatever
                 system feeds ours, and which one that is belongs to the deployment, not to the
                 field. The label lives in the lang group, so it is four locales rather than a
                 Cyrillic "1С:" hard-coded into the English, Armenian and Georgian UI too. */}
              {item.external_id ? (
                <Tag icon={<SyncOutlined />}>
                  {t("k_external_id")}: {textOf(item.external_id)}
                </Tag>
              ) : null}
            </div>
            {/* The date only: the counterparty is the "Клієнт" group's, and order type is in the
                badge row above — each value once on the screen. */}
            {item.datetime ? <Typography.Text type="secondary">{dayjs(item.datetime).format("DD.MM.YYYY HH:mm")}</Typography.Text> : null}
          </div>
        </div>
        {/* Always the same FOUR tiles — see `returns/ReturnDetail.js` for why a tile is never
            dropped when its value is missing. */}
        <KpiStrip
          items={[
            /* "До сплати" is the summary's "Всього до сплати" and the list's "Сума" — one figure. */
            { key: "total", label: translate(ORDERS_PAGE, "k_due", lang), value: formatMoney(item.payable_total) || dash },
            { key: "positions", label: translate(ORDERS_PAGE, "k_positions_units", lang), value: `${itemsCount} / ${unitsCount}` },
            {
              key: "discount",
              label: translate(ORDERS_PAGE, "k_discount_total", lang),
              /* The money and its share of the order together; a rise in price is not a discount
                 (the API's own rule), so a recalculation upwards leaves only the items discount. */
              value: isZero(item.discount_total)
                ? dash
                : [formatMoney(item.discount_total), item.discount_percent != null ? percent(item.discount_percent, 1) : null].filter(Boolean).join(" · "),
            },
            {
              key: "delivery",
              label: translate(ORDERS_PAGE, "k_delivery_date", lang),
              value: item.delivery_date ? dayjs(item.delivery_date).format("DD.MM.YYYY") : dash,
            },
          ]}
        />
      </div>
    </div>
  );
}

/* The items table's quantity and money columns, sized to what they hold (measured with the cells'
   own fonts, 16px of cell padding included): "Кількість" to its widest header of the four locales
   (ka "რაოდენობა", which wrapped at 104 in the real render; "2400" is 33px) → 112; "Ціна" → 96
   (sized when it also held a struck-through order total, 14px "2877816.64" ≈ 76px; kept); "Сума" to the final total (16px / 600,
   "2777816.64" ≈ 87px) → 104. Seven digits, not the sample's five: orders of 2.8 million exist, and
   at six-digit widths the old total wrapped mid-number and the final one ran into the card edge. ⚠ The fixed columns must stay
   under `SCROLL_X` with room left for "Назва": on the order page at 1024 the table is ~320px wide
   and scrolls, and a scroll width at or under the fixed sum leaves the name column 0px wide. */
const QTY_W = 112;
const PRICE_W = 96;
const SUM_W = 104;
const SCROLL_X = 48 + 112 + QTY_W + PRICE_W + SUM_W + 110;

/** The geolocation fact for a place with no map (the modals): the point count, escalating to the
    icon + red text when any point is fake — `isFake` is a fraud signal, so it has to be seen without
    opening the page. Shown when empty too ("no data"): no proof of where the order was taken is
    itself worth knowing. */
const GeoFact = ({ item, t }) => {
  const points = parseGeo(item.tracked_geolocation);
  const fake = points.filter(p => p.isFake).length;
  if (!points.length) return <Typography.Text type="secondary">{t("geo_no_data")}</Typography.Text>;
  if (fake)
    return (
      <Flex align="center" gap={6}>
        <StatusIcon Icon={WarningOutlined} tone="no" label={t("geo_fake_label")} />
        <Typography.Text type="danger">
          {t("geo_fake_count_label")}: {fake} / {points.length}
        </Typography.Text>
      </Flex>
    );
  return (
    <Typography.Text>
      {t("geo_points_count_label")}: {points.length}
    </Typography.Text>
  );
};

/**
 * Everything about the order that is not its items, in this order: "Деталі" (with the point),
 * "Контрагент", "Доставка". Each value is on the screen once — the head carries the number, status,
 * order type, external id and date, the tiles the money and the delivery date — and a field is
 * shown only when it holds something, the visit ("—": taken outside a visit) and the geolocation
 * ("no data") excepted. A group left with nothing is not drawn.
 *
 * `place`: "page" — the rail, where the map (`GeoSection`) follows these and the counterparty is
 * `Page.js`'s own card (`CounterpartySection`), passed in as `counterparty` so it takes its place
 * between the two groups; "modal" — one column under the items, with its own "Контрагент" (the
 * name row is "Назва", as the title says whose it is) and the geolocation fact.
 * `hideCounterparty`: the counterparty card's own modal, where that group is left out.
 */
export function OrderGroups({ item, lang, place = "modal", hideCounterparty = false, counterparty = null }) {
  const t = key => translate(ORDERS_PAGE, key, lang);
  const modal = place === "modal";
  const priceType = item.price_type ? translate(ORDERS_PAGE, `price_type_${item.price_type}`, lang) || item.price_type : null;
  /* The order's own discount % is told in the summary's "Знижка на позиції" row; it lands here only
     when that row is not drawn (no items discount), so it never disappears from the screen. */
  const orphanDiscount =
    Number(item.discount) && isZero(item.items_discount) ? { key: "discount", label: t("k_order_discount"), value: percent(item.discount, 0) } : null;
  return (
    <>
      <DetailGroup
        title={t("sec_details")}
        rows={[
          item.created_by ? { key: "created_by", label: t("k_created_by"), value: <PersonCell user={item.created_by} strong={false} /> } : null,
          {
            key: "visit",
            label: t("k_visit"),
            value: item.visit ? (
              <Typography.Link href={`/visit/${item.visit}`} target="_blank" rel="noopener noreferrer">
                {t("k_visit")} <LinkOutlined />
              </Typography.Link>
            ) : (
              dash
            ),
          },
          item.point?.name ? { key: "point", label: t("k_point"), value: textOf(item.point.name) } : null,
          item.supervisor ? { key: "supervisor", label: t("k_supervisor"), value: <PersonCell user={item.supervisor} strong={false} /> } : null,
          priceType ? { key: "price_type", label: t("k_price_type"), value: textOf(priceType) } : null,
          orphanDiscount,
          modal ? { key: "geo", label: t("k_tracked_geolocation"), value: <GeoFact item={item} t={t} /> } : null,
          item.status_changed
            ? {
                key: "status_changed",
                label: t("k_status_changed"),
                value: <span className="dp-nowrap">{dayjs(item.status_changed).format("DD.MM.YYYY HH:mm")}</span>,
              }
            : null,
        ]}
      />
      {modal ? (
        !hideCounterparty && item.counterparty ? (
          <DetailGroup
            title={t("k_counterparty")}
            rows={[
              { key: "name", label: t("k_name"), value: textOf(item.counterparty.name) },
              item.counterparty.address ? { key: "address", label: t("k_address"), value: textOf(item.counterparty.address) } : null,
            ]}
          />
        ) : null
      ) : (
        counterparty
      )}
      <DetailGroup
        title={t("sec_delivery")}
        rows={[
          isZero(item.shipping_cost)
            ? null
            : { key: "shipping", label: t("k_shipping_cost"), value: <span className="lp-num">{formatMoney(item.shipping_cost)}</span> },
        ]}
      />
    </>
  );
}

/* `layout`: "modal" (default) puts `OrderGroups` under the items; "page" leaves them to `Page.js`'s
   rail. `hideCounterparty` is for the counterparty card's Orders tab, whose modal is open over that
   very counterparty. */
const OrderDetail = ({ item, lang, showHeader = true, hideCounterparty = false, layout = "modal" }) => {
  const t = key => translate(ORDERS_PAGE, key, lang);

  /* One summary row: the label over the first five columns, the value under "Сума". */
  const summaryRow = (key, label, value, { first = false, final = false } = {}) => (
    <Table.Summary.Row key={key} style={{ background: "var(--surface-subtle)" }}>
      <Table.Summary.Cell index={0} colSpan={5} className={first ? "dp-summary-cell" : undefined}>
        {final ? <Typography.Text strong>{label}</Typography.Text> : <span className="dp-summary-label">{label}</span>}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={5} align="right" className={`dp-nowrap${first ? " dp-summary-cell" : ""}`}>
        {final ? (
          /* The one deliberately larger number in this card — 16px is `dp-sec-title`'s own step,
             matching `ReturnDetail`'s own emphasized summary figure. */
          <Typography.Text strong className="lp-num" style={{ fontSize: 16 }}>
            {value}
          </Typography.Text>
        ) : (
          <span className="lp-num">{value}</span>
        )}
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );

  const itemColumns = [
    /* The line's position in the order as the API sent it — derived from the render index, not
       stored: nothing on `OrderItem` records a line number. `pagination={false}`, so it is 1..N. */
    { key: "line", title: t("k_line_no"), width: 48, align: "right", render: (value, row, index) => <span className="lp-num">{index + 1}</span> },
    {
      key: "name",
      title: t("k_product_name"),
      ellipsis: true,
      render: (value, row) => textOf(row.product?.name),
    },
    { key: "code", title: t("k_product_code"), width: 112, render: (value, row) => textOf(row.product?.code) },
    {
      key: "quantity",
      title: t("k_quantity"),
      width: QTY_W,
      align: "right",
      render: (value, row) => <span className="lp-num">{textOf(row.quantity)}</span>,
    },
    {
      key: "price",
      title: t("k_price"),
      width: PRICE_W,
      align: "right",
      render: (value, row) =>
        recalculated(row) ? (
          <MoneyCell value={formatMoney(row.recalculated_price)} was={formatMoney(row.amount)} />
        ) : (
          <MoneyCell value={formatMoney(row.amount)} />
        ),
    },
    {
      key: "sum",
      title: t("k_amount"),
      width: SUM_W,
      align: "right",
      render: (value, row) =>
        recalculated(row) ? (
          <MoneyCell value={formatMoney(row.recalculated_line_total)} was={formatMoney(row.line_total)} />
        ) : (
          <MoneyCell value={formatMoney(row.line_total)} />
        ),
    },
  ];
  /* `recalculated_total_delta` is null exactly when the order has no recalculation. */
  const isRecalculated = item.recalculated_total_delta !== null && item.recalculated_total_delta !== undefined;

  return (
    <>
      {showHeader ? <OrderDetailHead item={item} lang={lang} /> : null}

      {/* Totals live as a footer of THIS table, not a separate "Підсумки" card — the same choice
         `returns/ReturnDetail.js` makes and for the same reason: the total READS as part of the
         table (it sits directly under "Сума", the column it sums) instead of a heading that
         merely happens to be nearby. Only the last row is emphasized, since "Всього" is the number
         someone opens this card to find. `dp-summary-cell` (`detail-page.scss`, shared with
         `ReturnDetail`'s own summary row) draws the divider as a class — `Table.Summary.Cell`
         does not forward a `style` prop at all, see that file for the full story. */}
      <div className="dp-card">
        <Typography.Text strong className="dp-sec-title">
          {t("sec_items")}
        </Typography.Text>
        {isRecalculated ? <span className="dp-sec-note">{t("k_recalc_note")}</span> : null}
        <Table
          rowKey="id"
          size="middle"
          columns={itemColumns}
          dataSource={item.items || []}
          pagination={false}
          scroll={{ x: SCROLL_X }}
          locale={{ emptyText: t("no_items") }}
          summary={() => (
            <Table.Summary fixed="bottom">
              {/* The items side of the order, ending in the amount due. A zero discount or
                  recalculation difference is no row at all; delivery is in the general information. */}
              {summaryRow("order_total", t("k_order_total"), formatMoney(item.order_total), { first: true })}
              {/* The order's discount % rides on the row it produced. */}
              {isZero(item.items_discount)
                ? null
                : summaryRow(
                    "items_discount",
                    Number(item.discount) ? `${t("k_items_discount")} · ${percent(item.discount, 0)}` : t("k_items_discount"),
                    signed(-Number(item.items_discount))
                  )}
              {isZero(item.recalculated_total_delta) ? null : summaryRow("recalc", t("k_recalc_diff"), signed(item.recalculated_total_delta))}
              {summaryRow("due", t("k_total_due"), formatMoney(item.payable_total), { final: true })}
            </Table.Summary>
          )}
        />
      </div>
      {/* A comment carries the reason behind the order, which reads as content, not metadata —
         its own card under the items (`additional_info` is dropped, see `returns/ReturnDetail.js`). */}
      {item.comments ? (
        <div className="dp-card">
          <Typography.Text strong className="dp-sec-title">
            {t("k_comment")}
          </Typography.Text>
          <div className="dp-note__box">{textOf(item.comments)}</div>
        </div>
      ) : null}
      {layout === "modal" ? <OrderGroups item={item} lang={lang} place="modal" hideCounterparty={hideCounterparty} /> : null}
    </>
  );
};

export default OrderDetail;
